import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  FaArrowLeft,
  FaBroadcastTower,
  FaStopCircle,
  FaVideo,
  FaVideoSlash,
  FaEye,
  FaUserTimes,
  FaMicrophone,
  FaMicrophoneSlash,
  FaSyncAlt,
  FaHeart,
  FaWallet,
} from "react-icons/fa";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";

import "../css/GoLivePage.css";
import useLiveCountdown from "./useLiveCountdown";

const RTC_CONFIGURATION = {
  iceServers: [
    {
      urls: "stun:stun.l.google.com:19302",
    },
    {
      urls: "stun:stun1.l.google.com:19302",
    },
    {
      urls: "stun:stun2.l.google.com:19302",
    },
  ],
};

function GoLivePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ==========================
  // MEDIA
  // ==========================

  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const guestRemoteVideoRef = useRef(null);

  const acceptedGuestRef = useRef(null);

  const [activeGuest, setActiveGuest] = useState(null);

  const [guestConnected, setGuestConnected] = useState(false);

  // ==========================
  // LIVE WEBSOCKET
  // ==========================

  const liveStompClientRef = useRef(null);

  // ==========================
  // WEBRTC
  // One PeerConnection
  // for each viewer
  // ==========================

  const viewerPeersRef = useRef(new Map());

  const viewerCandidatesRef = useRef(new Map());

  // ==========================
  // STATE
  // ==========================

  const [guestRequest, setGuestRequest] = useState(null);

  const [guestRequestProcessing, setGuestRequestProcessing] = useState(false);

  const [title, setTitle] = useState("");

  const [cameraReady, setCameraReady] = useState(false);

  const [cameraLoading, setCameraLoading] = useState(false);

  const [cameraError, setCameraError] = useState("");

  const [showViewers, setShowViewers] = useState(false);

  const [liveReactions, setLiveReactions] = useState([]);

  const [apiError, setApiError] = useState("");

  const [startingLive, setStartingLive] = useState(false);

  const [endingLive, setEndingLive] = useState(false);

  const [activeLive, setActiveLive] = useState(null);

  const [liveComments, setLiveComments] = useState([]);

  const [liveGifts, setLiveGifts] = useState([]);

  const liveViewersRef = useRef(new Set());

  const [viewerCount, setViewerCount] = useState(0);

  const [creatorEarnings, setCreatorEarnings] = useState(0);

  const [micEnabled, setMicEnabled] = useState(true);

  const [cameraEnabled, setCameraEnabled] = useState(true);

  const [facingMode, setFacingMode] = useState("user");

  const [switchingCamera, setSwitchingCamera] = useState(false);

  const [showEndConfirm, setShowEndConfirm] = useState(false);

  const [liveViewerUsers, setLiveViewerUsers] = useState([]);
  // ==========================
  // AUTH
  // ==========================

  const token = localStorage.getItem("authToken");

  const authorizationHeader = useMemo(() => {
    if (!token) {
      return null;
    }

    return token.startsWith("Bearer ") ? token : `Bearer ${token}`;
  }, [token]);

  const fetchCreatorEarnings = useCallback(async () => {
    if (!authorizationHeader) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/earnings/me`,
        {
          headers: {
            Authorization: authorizationHeader,
          },
        },
      );

      if (!response.ok) {
        console.warn("Unable to load creator earnings:", response.status);

        return;
      }

      const data = await response.json();

      setCreatorEarnings(Number(data?.availableEarnings || 0));
    } catch (error) {
      console.error("❌ Creator earnings error:", error);
    }
  }, [authorizationHeader]);

  useEffect(() => {
    fetchCreatorEarnings();
  }, [fetchCreatorEarnings]);

  const currentUser = useMemo(() => {
    try {
      const stored = localStorage.getItem("currentUser");

      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error("Unable to read current user:", error);

      return null;
    }
  }, []);

  const recoverActiveLive = useCallback(async () => {
    if (!authorizationHeader) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/live/my-active`,
        {
          headers: {
            Authorization: authorizationHeader,
          },
        },
      );

      if (response.status === 204) {
        console.log("ℹ️ No active live to recover");

        return;
      }

      if (!response.ok) {
        throw new Error(`Unable to recover live: ${response.status}`);
      }

      const data = await response.json();

      console.log("♻️ Active live recovered:", data);

      liveViewersRef.current.clear();

      setViewerCount(0);

      setActiveLive(data);
    } catch (error) {
      console.error("Unable to recover active live:", error);
    }
  }, [authorizationHeader]);

  useEffect(() => {
    recoverActiveLive();
  }, [recoverActiveLive]);

  const handleKickViewer = useCallback(
    (viewerId) => {
      if (!activeLive?.id || !currentUser?.id) {
        return;
      }

      const viewerKey = String(viewerId);

      console.log("🚫 Removing viewer:", viewerId);

      // ==========================
      // SEND KICK TO VIEWER
      // ==========================

      if (liveStompClientRef.current?.connected) {
        liveStompClientRef.current.publish({
          destination: `/app/live/${activeLive.id}`,

          body: JSON.stringify({
            liveId: activeLive.id,

            fromUserId: currentUser.id,

            toUserId: viewerId,

            type: "KICK_VIEWER",
          }),
        });
      }

      // ==========================
      // CLOSE WEBRTC
      // ==========================

      const peer = viewerPeersRef.current.get(viewerKey);

      if (peer) {
        try {
          peer.close();
        } catch (error) {
          console.warn("Unable to close viewer:", error);
        }
      }

      viewerPeersRef.current.delete(viewerKey);

      viewerCandidatesRef.current.delete(viewerKey);

      liveViewersRef.current.delete(viewerKey);

      // ==========================
      // REMOVE FROM UI
      // ==========================

      setLiveViewerUsers((previous) =>
        previous.filter((viewer) => String(viewer.id) !== viewerKey),
      );

      const count = liveViewersRef.current.size;

      setViewerCount(count);

      // ==========================
      // UPDATE EVERYONE'S COUNT
      // ==========================

      if (liveStompClientRef.current?.connected) {
        liveStompClientRef.current.publish({
          destination: `/app/live/${activeLive.id}`,

          body: JSON.stringify({
            liveId: activeLive.id,

            fromUserId: currentUser.id,

            type: "VIEWER_COUNT",

            viewerCount: count,
          }),
        });
      }

      console.log("✅ Viewer removed:", viewerId);
    },
    [activeLive?.id, currentUser?.id],
  );

  const addLiveReaction = useCallback((userName) => {
    const reaction = {
      id: `${Date.now()}-${Math.random()}`,

      name: userName || "User",

      right: 14 + Math.random() * 28,

      size: 18 + Math.random() * 16,

      duration: 1.8 + Math.random() * 0.8,
    };

    setLiveReactions((previous) => [...previous, reaction]);

    window.setTimeout(() => {
      setLiveReactions((previous) =>
        previous.filter((item) => item.id !== reaction.id),
      );
    }, 3000);
  }, []);

  // ==========================
  // LIVE COMMENTS
  // ==========================

  const addLiveComment = useCallback(({ userName, message }) => {
    const cleanMessage = String(message || "").trim();

    if (!cleanMessage) {
      return;
    }

    const comment = {
      id: `${Date.now()}-${Math.random()}`,

      userName: userName || "User",

      message: cleanMessage.slice(0, 160),
    };

    /*
     * Keep only the latest
     * comments visible.
     */
    setLiveComments((previous) => [...previous.slice(-4), comment]);

    /*
     * Remove from screen
     * after 12 seconds.
     */
    window.setTimeout(() => {
      setLiveComments((previous) =>
        previous.filter((item) => item.id !== comment.id),
      );
    }, 12000);
  }, []);

  // ==========================
  // LIVE GIFTS
  // ==========================

  const addLiveGift = useCallback(({ userName, emoji, label }) => {
    const gift = {
      id: `${Date.now()}-${Math.random()}`,

      userName: userName || "User",

      emoji: emoji || "🎁",

      label: label || "Gift",

      /*
       * Slightly random horizontal
       * position for nicer animation.
       */
      left: 35 + Math.random() * 30,
    };

    setLiveGifts((previous) => [...previous.slice(-3), gift]);

    window.setTimeout(() => {
      setLiveGifts((previous) =>
        previous.filter((item) => item.id !== gift.id),
      );
    }, 3800);
  }, []);

  // ==========================
  // CLOSE ALL VIEWER PEERS
  // ==========================

  const handleRemoveGuest = useCallback(() => {
    if (!activeGuest?.viewerId || !activeLive?.id || !currentUser?.id) {
      return;
    }

    if (liveStompClientRef.current?.connected) {
      liveStompClientRef.current.publish({
        destination: `/app/live/${activeLive.id}`,

        body: JSON.stringify({
          liveId: activeLive.id,

          fromUserId: currentUser.id,

          toUserId: activeGuest.viewerId,

          type: "GUEST_REMOVED",
        }),
      });
    }

    acceptedGuestRef.current = null;

    setActiveGuest(null);

    setGuestConnected(false);

    if (guestRemoteVideoRef.current) {
      guestRemoteVideoRef.current.srcObject = null;
    }
  }, [activeGuest, activeLive?.id, currentUser?.id]);

  const closeAllViewerPeers = useCallback(() => {
    viewerPeersRef.current.forEach((peer) => {
      try {
        peer.close();
      } catch (error) {
        console.warn("Unable to close viewer peer:", error);
      }
    });

    viewerPeersRef.current.clear();

    viewerCandidatesRef.current.clear();
  }, []);

  // ==========================
  // STOP CAMERA
  // ==========================

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });

      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setCameraReady(false);
    setCameraLoading(false);
  }, []);

  // ==========================
  // START CAMERA
  // ==========================

  const startCamera = useCallback(async () => {
    if (streamRef.current) {
      return streamRef.current;
    }

    setCameraError("");
    setCameraLoading(true);

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Camera access is not supported by this browser.");
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",

          width: {
            ideal: 1280,
          },

          height: {
            ideal: 720,
          },
        },

        audio: true,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;

        try {
          await videoRef.current.play();
        } catch (playError) {
          console.warn("Video autoplay was blocked:", playError);
        }
      }

      setCameraReady(true);
      setCameraLoading(false);

      return stream;
    } catch (error) {
      console.error("Unable to open camera:", error);

      setCameraReady(false);

      setCameraLoading(false);

      setCameraError(
        error?.message ||
          t("live.cameraError", "Unable to access your camera and microphone."),
      );

      return null;
    }
  }, [t]);

  // ==========================
  // OPEN CAMERA ON PAGE LOAD
  // ==========================

  useEffect(() => {
    startCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          track.stop();
        });

        streamRef.current = null;
      }
    };
  }, [startCamera]);

  // ==========================
  // LIVE SIGNALING
  // ==========================

  useEffect(() => {
    if (
      !activeLive?.id ||
      String(activeLive.status).toUpperCase() !== "LIVE" ||
      !currentUser?.id ||
      !token
    ) {
      return undefined;
    }

    console.log("🔴 Host connecting to live signaling:", activeLive.id);

    const socket = new SockJS(
      `${process.env.REACT_APP_API_URL}/ws?token=${encodeURIComponent(token)}`,
    );

    const client = new Client({
      webSocketFactory: () => socket,

      connectHeaders: {
        Authorization: authorizationHeader,
      },

      reconnectDelay: 5000,

      heartbeatIncoming: 4000,

      heartbeatOutgoing: 4000,

      onConnect: () => {
        console.log("✅ Host connected to live WebSocket");

        liveStompClientRef.current = client;

        const publishViewerCount = () => {
          const count = liveViewersRef.current.size;

          setViewerCount(count);

          if (!client.connected) {
            return;
          }

          client.publish({
            destination: `/app/live/${activeLive.id}`,

            body: JSON.stringify({
              liveId: activeLive.id,

              fromUserId: currentUser.id,

              type: "VIEWER_COUNT",

              viewerCount: count,
            }),
          });

          console.log("👁 Viewer count:", count);
        };

        client.subscribe(
          `/topic/live/${activeLive.id}`,

          async (message) => {
            try {
              const payload = JSON.parse(message.body);

              console.log("📡 HOST LIVE MESSAGE:", payload);

              // Ignore messages
              // sent by host himself.

              if (String(payload.fromUserId) === String(currentUser.id)) {
                return;
              }

              // ==========================
              // LIVE HEART REACTION
              // ==========================

              if (payload.type === "REACTION_HEART") {
                addLiveReaction(payload.fromUserName);

                console.log(`❤️ ${payload.fromUserName} reacted`);

                return;
              }

              // ==========================
              // LIVE COMMENT
              // ==========================

              if (payload.type === "LIVE_COMMENT") {
                addLiveComment({
                  userName: payload.fromUserName,

                  message: payload.message,
                });

                console.log(
                  "💬 Live comment:",
                  payload.fromUserName,
                  payload.message,
                );

                return;
              }

              if (payload.type === "LIVE_GIFT") {
                // =====================================
                // SHOW GIFT ANIMATION
                // =====================================

                addLiveGift({
                  userName: payload.fromUserName,

                  emoji: payload.giftEmoji,

                  label: payload.giftLabel,
                });

                // =====================================
                // UPDATE CREATOR EARNINGS
                // =====================================

                const earned = Number(payload.creatorEarning || 0);

                if (Number.isFinite(earned) && earned > 0) {
                  setCreatorEarnings((previous) =>
                    Number((previous + earned).toFixed(2)),
                  );
                }

                console.log(
                  "🎁 Live gift:",
                  payload.fromUserName,
                  payload.giftLabel,
                  "| creator earned:",
                  earned,
                );

                return;
              }

              // ==========================
              // VIEWER COUNT REQUEST
              // ==========================

              if (
                payload.type === "VIEWER_COUNT_REQUEST" &&
                String(payload.toUserId) === String(currentUser.id)
              ) {
                console.log(
                  "👁 Viewer requested current count:",
                  payload.fromUserId,
                );

                publishViewerCount();

                return;
              }

              // ==========================
              // GUEST REQUEST
              // ==========================

              if (
                payload.type === "GUEST_REQUEST" &&
                String(payload.toUserId) === String(currentUser.id)
              ) {
                const viewerId = payload.fromUserId;

                let viewerName = payload.fromUserName || `User ${viewerId}`;

                let viewerImage = null;

                try {
                  const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/users/${viewerId}/details`,
                    {
                      headers: {
                        Authorization: authorizationHeader,
                      },
                    },
                  );

                  if (response.ok) {
                    const viewer = await response.json();

                    viewerName = viewer.name || viewer.username || viewerName;

                    viewerImage =
                      viewer.mediaUrls?.[0] || viewer.profilePicture || null;
                  }
                } catch (error) {
                  console.warn("Unable to load guest request profile:", error);
                }

                setGuestRequest({
                  viewerId,
                  viewerName,
                  viewerImage,
                });

                console.log("🙋 Guest request received:", viewerId);

                return;
              }

              // =====================================
              // VIEWER RECONNECT REQUEST
              // =====================================

              if (
                payload.type === "RECONNECT_REQUEST" &&
                String(payload.toUserId) === String(currentUser.id)
              ) {
                const viewerId = payload.fromUserId;

                const viewerKey = String(viewerId);

                console.log("🔄 Viewer requested WebRTC reconnect:", viewerId);

                // Close old connection
                const oldPeer = viewerPeersRef.current.get(viewerKey);

                if (oldPeer) {
                  try {
                    oldPeer.close();
                  } catch (error) {
                    console.warn("Unable to close old reconnect peer:", error);
                  }
                }

                viewerPeersRef.current.delete(viewerKey);

                viewerCandidatesRef.current.delete(viewerKey);

                // =====================================
                // CREATE NEW PEER
                // =====================================

                const peer = new RTCPeerConnection(RTC_CONFIGURATION);

                viewerPeersRef.current.set(viewerKey, peer);

                // =====================================
                // ADD HOST CAMERA + MICROPHONE
                // =====================================

                if (!streamRef.current) {
                  console.error("❌ Host stream missing during reconnect");

                  return;
                }

                streamRef.current.getTracks().forEach((track) => {
                  peer.addTrack(track, streamRef.current);
                });

                // =====================================
                // HOST ICE
                // =====================================

                peer.onicecandidate = (event) => {
                  if (
                    !event.candidate ||
                    !liveStompClientRef.current?.connected
                  ) {
                    return;
                  }

                  liveStompClientRef.current.publish({
                    destination: `/app/live/${activeLive.id}`,

                    body: JSON.stringify({
                      liveId: activeLive.id,

                      fromUserId: currentUser.id,

                      toUserId: viewerId,

                      type: "CANDIDATE",

                      candidate: event.candidate.toJSON(),
                    }),
                  });
                };

                // =====================================
                // CONNECTION STATE
                // =====================================

                peer.onconnectionstatechange = () => {
                  const state = peer.connectionState;

                  console.log(`🌐 Reconnected viewer ${viewerId}:`, state);

                  if (state === "connected") {
                    liveViewersRef.current.add(viewerKey);

                    publishViewerCount();

                    console.log(
                      "✅ Viewer reconnected successfully:",
                      viewerKey,
                    );

                    return;
                  }

                  if (state === "failed" || state === "closed") {
                    if (viewerPeersRef.current.get(viewerKey) === peer) {
                      viewerPeersRef.current.delete(viewerKey);

                      viewerCandidatesRef.current.delete(viewerKey);
                    }
                  }
                };

                // =====================================
                // CREATE NEW OFFER
                // =====================================

                const offer = await peer.createOffer({
                  iceRestart: true,
                });

                await peer.setLocalDescription(offer);

                if (liveStompClientRef.current?.connected) {
                  liveStompClientRef.current.publish({
                    destination: `/app/live/${activeLive.id}`,

                    body: JSON.stringify({
                      liveId: activeLive.id,

                      fromUserId: currentUser.id,

                      toUserId: viewerId,

                      type: "OFFER",

                      sdp: peer.localDescription?.sdp || offer.sdp,
                    }),
                  });

                  console.log("📤 Reconnect OFFER sent:", viewerId);
                }

                return;
              }

              // ==========================
              // VIEWER JOINED
              // ==========================

              if (
                payload.type === "JOIN" &&
                String(payload.toUserId) === String(currentUser.id)
              ) {
                const viewerId = payload.fromUserId;

                const viewerKey = String(viewerId);

                console.log("👤 Viewer joined:", viewerId);

                // --------------------------
                // Load viewer information
                // --------------------------

                let viewerName = `User ${viewerId}`;

                let viewerImage = null;

                try {
                  const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/users/${viewerId}/details`,

                    {
                      headers: {
                        Authorization: authorizationHeader,
                      },
                    },
                  );

                  if (response.ok) {
                    const user = await response.json();

                    viewerName = user.name || viewerName;

                    viewerImage = user.mediaUrls?.[0] || null;
                  }
                } catch (userError) {
                  console.warn("Could not load viewer:", userError);
                }

                setLiveViewerUsers((previous) => {
                  const withoutSameViewer = previous.filter(
                    (viewer) => String(viewer.id) !== String(viewerId),
                  );

                  return [
                    ...withoutSameViewer,
                    {
                      id: viewerId,
                      name: viewerName,
                      image: viewerImage,
                    },
                  ];
                });

                // --------------------------
                // Close old peer
                // for same viewer
                // --------------------------

                const existingPeer = viewerPeersRef.current.get(viewerKey);

                if (existingPeer) {
                  try {
                    existingPeer.close();
                  } catch (closeError) {
                    console.warn("Unable to close old peer:", closeError);
                  }

                  viewerPeersRef.current.delete(viewerKey);
                }

                viewerCandidatesRef.current.delete(viewerKey);

                // --------------------------
                // Create peer
                // --------------------------

                const peer = new RTCPeerConnection(RTC_CONFIGURATION);

                viewerPeersRef.current.set(viewerKey, peer);

                // --------------------------
                // Add host camera
                // and microphone
                // --------------------------

                if (streamRef.current) {
                  streamRef.current.getTracks().forEach((track) => {
                    peer.addTrack(track, streamRef.current);
                  });
                } else {
                  console.error("❌ Host media stream is missing.");

                  return;
                }

                // --------------------------
                // HOST ICE
                // --------------------------

                peer.onicecandidate = (event) => {
                  if (
                    !event.candidate ||
                    !liveStompClientRef.current?.connected
                  ) {
                    return;
                  }

                  liveStompClientRef.current.publish({
                    destination: `/app/live/${activeLive.id}`,

                    body: JSON.stringify({
                      liveId: activeLive.id,

                      fromUserId: currentUser.id,

                      toUserId: viewerId,

                      type: "CANDIDATE",

                      candidate: event.candidate.toJSON(),
                    }),
                  });

                  console.log("🧊 Host ICE sent to viewer:", viewerId);
                };

                peer.onconnectionstatechange = () => {
                  console.log(
                    `🌐 Viewer ${viewerId} WebRTC:`,
                    peer.connectionState,
                  );

                  if (peer.connectionState === "connected") {
                    if (!liveViewersRef.current.has(viewerKey)) {
                      liveViewersRef.current.add(viewerKey);

                      publishViewerCount();
                    }

                    console.log("✅ Viewer truly connected:", viewerKey);
                  }

                  // =====================================
                  // FAILED / CLOSED
                  // =====================================

                  if (
                    peer.connectionState === "failed" ||
                    peer.connectionState === "closed"
                  ) {
                    // =====================================
                    // IMPORTANT:
                    // Ignore an old peer if a new peer
                    // has already replaced it.
                    // =====================================

                    if (viewerPeersRef.current.get(viewerKey) !== peer) {
                      console.log(
                        "♻️ Old failed/closed peer ignored:",
                        viewerKey,
                      );

                      return;
                    }

                    viewerPeersRef.current.delete(viewerKey);

                    viewerCandidatesRef.current.delete(viewerKey);

                    liveViewersRef.current.delete(viewerKey);

                    setLiveViewerUsers((previous) =>
                      previous.filter(
                        (viewer) => String(viewer.id) !== String(viewerKey),
                      ),
                    );

                    publishViewerCount();

                    console.log(
                      "👋 Viewer removed after WebRTC close:",
                      viewerKey,
                    );

                    return;
                  }

                  // =====================================
                  // DISCONNECTED
                  // Wait 5 seconds before removing
                  // =====================================

                  if (peer.connectionState === "disconnected") {
                    setTimeout(() => {
                      // A new peer may already have been created
                      // by RECONNECT_REQUEST.
                      // In that case, do NOT remove the new connection.
                      if (viewerPeersRef.current.get(viewerKey) !== peer) {
                        console.log(
                          "♻️ Old disconnected peer ignored:",
                          viewerKey,
                        );

                        return;
                      }

                      if (
                        peer.connectionState === "disconnected" ||
                        peer.connectionState === "failed" ||
                        peer.connectionState === "closed"
                      ) {
                        viewerPeersRef.current.delete(viewerKey);

                        viewerCandidatesRef.current.delete(viewerKey);

                        liveViewersRef.current.delete(viewerKey);

                        setLiveViewerUsers((previous) =>
                          previous.filter(
                            (viewer) => String(viewer.id) !== String(viewerKey),
                          ),
                        );

                        publishViewerCount();

                        console.log(
                          "👋 Disconnected viewer removed:",
                          viewerKey,
                        );
                      }
                    }, 5000);
                  }
                };

                peer.oniceconnectionstatechange = () => {
                  console.log(
                    `🧊 Viewer ${viewerId} ICE:`,
                    peer.iceConnectionState,
                  );
                };

                // --------------------------
                // CREATE OFFER
                // --------------------------

                const offer = await peer.createOffer();

                await peer.setLocalDescription(offer);

                liveStompClientRef.current.publish({
                  destination: `/app/live/${activeLive.id}`,

                  body: JSON.stringify({
                    liveId: activeLive.id,

                    fromUserId: currentUser.id,

                    toUserId: viewerId,

                    type: "OFFER",

                    sdp: peer.localDescription?.sdp || offer.sdp,
                  }),
                });

                console.log("📤 OFFER sent to viewer:", viewerId);

                return;
              }

              // =====================================
              // GUEST MEDIA OFFER
              // =====================================

              if (
                payload.type === "GUEST_OFFER" &&
                String(payload.toUserId) === String(currentUser.id)
              ) {
                const viewerId = payload.fromUserId;

                const viewerKey = String(viewerId);

                const accepted = acceptedGuestRef.current;

                // Security / consistency:
                // only the accepted guest can publish media.
                if (!accepted || String(accepted.viewerId) !== viewerKey) {
                  console.warn(
                    "⚠️ Guest offer rejected - viewer was not accepted:",
                    viewerId,
                  );

                  return;
                }

                const peer = viewerPeersRef.current.get(viewerKey);

                if (!peer || !payload.sdp) {
                  console.error("❌ Peer or guest SDP missing");

                  return;
                }

                console.log("📥 Guest OFFER received:", viewerId);

                // =====================================
                // RECEIVE GUEST CAMERA + MICROPHONE
                // =====================================

                peer.ontrack = async (event) => {
                  console.log("🎥 Guest track received:", event.track?.kind);

                  const video = guestRemoteVideoRef.current;

                  if (!video) {
                    return;
                  }

                  let stream = event.streams?.[0];

                  if (!stream) {
                    if (video.srcObject instanceof MediaStream) {
                      stream = video.srcObject;
                    } else {
                      stream = new MediaStream();
                    }

                    const exists = stream
                      .getTracks()
                      .some((track) => track.id === event.track.id);

                    if (!exists) {
                      stream.addTrack(event.track);
                    }
                  }

                  if (video.srcObject !== stream) {
                    video.srcObject = stream;
                  }

                  video.muted = false;

                  try {
                    await video.play();
                  } catch (error) {
                    console.warn("Guest autoplay blocked:", error);
                  }

                  setGuestConnected(true);
                };

                // =====================================
                // APPLY GUEST OFFER
                // =====================================

                await peer.setRemoteDescription(
                  new RTCSessionDescription({
                    type: "offer",

                    sdp: payload.sdp,
                  }),
                );

                // =====================================
                // HOST ANSWERS RENEGOTIATION
                // =====================================

                const answer = await peer.createAnswer();

                await peer.setLocalDescription(answer);

                if (liveStompClientRef.current?.connected) {
                  liveStompClientRef.current.publish({
                    destination: `/app/live/${activeLive.id}`,

                    body: JSON.stringify({
                      liveId: activeLive.id,

                      fromUserId: currentUser.id,

                      toUserId: viewerId,

                      type: "GUEST_ANSWER",

                      sdp: peer.localDescription?.sdp || answer.sdp,
                    }),
                  });

                  console.log("📤 GUEST_ANSWER sent:", viewerId);
                }

                return;
              }

              // =====================================
              // GUEST LEFT PARTICIPATION
              // =====================================

              if (
                payload.type === "GUEST_LEFT" &&
                String(payload.toUserId) === String(currentUser.id)
              ) {
                const viewerId = String(payload.fromUserId);

                if (String(acceptedGuestRef.current?.viewerId) === viewerId) {
                  acceptedGuestRef.current = null;

                  setActiveGuest(null);

                  setGuestConnected(false);

                  if (guestRemoteVideoRef.current) {
                    guestRemoteVideoRef.current.srcObject = null;
                  }

                  console.log("👋 Guest returned to viewer mode:", viewerId);
                }

                return;
              }

              if (
                payload.type === "GUEST_LEAVE_OFFER" &&
                String(payload.toUserId) === String(currentUser.id)
              ) {
                const viewerKey = String(payload.fromUserId);

                const peer = viewerPeersRef.current.get(viewerKey);

                if (!peer || !payload.sdp) {
                  return;
                }

                await peer.setRemoteDescription(
                  new RTCSessionDescription({
                    type: "offer",

                    sdp: payload.sdp,
                  }),
                );

                const answer = await peer.createAnswer();

                await peer.setLocalDescription(answer);

                if (liveStompClientRef.current?.connected) {
                  liveStompClientRef.current.publish({
                    destination: `/app/live/${activeLive.id}`,

                    body: JSON.stringify({
                      liveId: activeLive.id,

                      fromUserId: currentUser.id,

                      toUserId: payload.fromUserId,

                      type: "GUEST_LEAVE_ANSWER",

                      sdp: peer.localDescription?.sdp || answer.sdp,
                    }),
                  });
                }

                return;
              }

              // ==========================
              // VIEWER ANSWER
              // ==========================

              if (
                payload.type === "ANSWER" &&
                String(payload.toUserId) === String(currentUser.id)
              ) {
                const viewerKey = String(payload.fromUserId);

                const peer = viewerPeersRef.current.get(viewerKey);

                if (!peer) {
                  console.warn(
                    "⚠️ ANSWER received but viewer peer does not exist:",
                    viewerKey,
                  );

                  return;
                }

                if (peer.signalingState !== "have-local-offer") {
                  console.warn(
                    "⚠️ Ignoring ANSWER. Current signaling state:",
                    peer.signalingState,
                  );

                  return;
                }

                await peer.setRemoteDescription({
                  type: "answer",

                  sdp: payload.sdp,
                });

                console.log("📥 ANSWER received from viewer:", viewerKey);

                // --------------------------
                // Apply queued viewer ICE
                // --------------------------

                const queuedCandidates =
                  viewerCandidatesRef.current.get(viewerKey) || [];

                for (const candidate of queuedCandidates) {
                  try {
                    await peer.addIceCandidate(new RTCIceCandidate(candidate));
                  } catch (candidateError) {
                    console.warn(
                      "Unable to add queued viewer ICE:",
                      candidateError,
                    );
                  }
                }

                viewerCandidatesRef.current.delete(viewerKey);

                return;
              }

              // ==========================
              // VIEWER ICE CANDIDATE
              // ==========================

              if (
                payload.type === "CANDIDATE" &&
                String(payload.toUserId) === String(currentUser.id) &&
                payload.candidate
              ) {
                const viewerKey = String(payload.fromUserId);

                const peer = viewerPeersRef.current.get(viewerKey);

                if (peer && peer.remoteDescription) {
                  try {
                    await peer.addIceCandidate(
                      new RTCIceCandidate(payload.candidate),
                    );

                    console.log("🧊 Viewer ICE added:", viewerKey);
                  } catch (candidateError) {
                    console.error("Unable to add viewer ICE:", candidateError);
                  }
                } else {
                  const queuedCandidates =
                    viewerCandidatesRef.current.get(viewerKey) || [];

                  queuedCandidates.push(payload.candidate);

                  viewerCandidatesRef.current.set(viewerKey, queuedCandidates);

                  console.log("⏳ Viewer ICE queued:", viewerKey);
                }

                return;
              }

              // ==========================
              // VIEWER LEFT
              // ==========================

              if (
                payload.type === "LEAVE" &&
                String(payload.toUserId) === String(currentUser.id)
              ) {
                const viewerKey = String(payload.fromUserId);

                const peer = viewerPeersRef.current.get(viewerKey);

                if (peer) {
                  try {
                    peer.close();
                  } catch (closeError) {
                    console.warn("Unable to close viewer peer:", closeError);
                  }
                }

                viewerPeersRef.current.delete(viewerKey);

                viewerCandidatesRef.current.delete(viewerKey);

                liveViewersRef.current.delete(viewerKey);

                setLiveViewerUsers((previous) =>
                  previous.filter(
                    (viewer) => String(viewer.id) !== String(viewerKey),
                  ),
                );

                publishViewerCount();

                console.log("👋 Viewer left:", viewerKey);

                return;
              }
            } catch (error) {
              console.error("❌ Invalid live signaling message:", error);
            }
          },
        );
      },

      onStompError: (frame) => {
        console.error("❌ Host STOMP error:", frame.headers?.message);
      },

      onWebSocketError: (event) => {
        console.error("❌ Host WebSocket error:", event);
      },
    });

    liveStompClientRef.current = client;

    client.activate();

    return () => {
      console.log("🧹 Cleaning host live signaling");

      closeAllViewerPeers();

      try {
        client.deactivate();
      } catch (error) {
        console.warn("Unable to deactivate live WebSocket:", error);
      }

      liveStompClientRef.current = null;
    };
  }, [
    activeLive?.id,
    activeLive?.status,
    currentUser?.id,
    token,
    authorizationHeader,
    closeAllViewerPeers,
    addLiveReaction,
    addLiveComment,
    addLiveGift,
  ]);

  const handleAcceptGuest = useCallback(() => {
    if (
      !guestRequest?.viewerId ||
      !activeLive?.id ||
      !currentUser?.id ||
      !liveStompClientRef.current?.connected ||
      guestRequestProcessing
    ) {
      return;
    }

    // =====================================
    // ONLY ONE ACTIVE GUEST AT A TIME
    // =====================================

    if (activeGuest?.viewerId) {
      console.warn("⚠️ A guest is already active:", activeGuest.viewerId);

      setApiError(
        t("live.onlyOneGuest", "Only one guest can join the live at a time."),
      );

      return;
    }

    setGuestRequestProcessing(true);

    try {
      liveStompClientRef.current.publish({
        destination: `/app/live/${activeLive.id}`,

        body: JSON.stringify({
          liveId: activeLive.id,

          fromUserId: currentUser.id,

          toUserId: guestRequest.viewerId,

          type: "GUEST_ACCEPTED",
        }),
      });

      const acceptedGuest = {
        viewerId: guestRequest.viewerId,

        viewerName: guestRequest.viewerName,

        viewerImage: guestRequest.viewerImage,
      };

      acceptedGuestRef.current = acceptedGuest;

      setActiveGuest(acceptedGuest);

      setGuestConnected(false);

      console.log("✅ Guest accepted:", guestRequest.viewerId);

      setGuestRequest(null);
    } finally {
      setGuestRequestProcessing(false);
    }
  }, [
    guestRequest,
    activeLive?.id,
    currentUser?.id,
    guestRequestProcessing,
    activeGuest?.viewerId,
    t,
  ]);

  const handleDeclineGuest = useCallback(() => {
    if (
      !guestRequest?.viewerId ||
      !activeLive?.id ||
      !currentUser?.id ||
      !liveStompClientRef.current?.connected ||
      guestRequestProcessing
    ) {
      return;
    }

    setGuestRequestProcessing(true);

    try {
      liveStompClientRef.current.publish({
        destination: `/app/live/${activeLive.id}`,

        body: JSON.stringify({
          liveId: activeLive.id,

          fromUserId: currentUser.id,

          toUserId: guestRequest.viewerId,

          type: "GUEST_REJECTED",
        }),
      });

      console.log("❌ Guest declined:", guestRequest.viewerId);

      setGuestRequest(null);
    } finally {
      setGuestRequestProcessing(false);
    }
  }, [guestRequest, activeLive?.id, currentUser?.id, guestRequestProcessing]);

  // ==========================
  // START LIVE
  // ==========================

  const handleStartLive = async () => {
    if (!authorizationHeader) {
      setApiError(
        t(
          "live.authenticationRequired",
          "You must be logged in to start a live.",
        ),
      );

      return;
    }

    setApiError("");

    let stream = streamRef.current;

    if (!stream) {
      stream = await startCamera();
    }

    if (!stream) {
      setApiError(
        t("live.cameraRequired", "Please allow camera and microphone access."),
      );

      return;
    }

    setStartingLive(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/live/start`,

        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",

            Authorization: authorizationHeader,
          },

          body: JSON.stringify({
            title: title.trim(),
          }),
        },
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.message || `Unable to start live: ${response.status}`,
        );
      }

      liveViewersRef.current.clear();
      setViewerCount(0);

      setActiveLive(data);

      window.dispatchEvent(new CustomEvent("refresh-live-sessions"));

      console.log("🔴 Live session started:", data);
    } catch (error) {
      console.error("Unable to start live:", error);

      setApiError(
        error?.message ||
          t("live.startError", "Unable to start the live session."),
      );
    } finally {
      setStartingLive(false);
    }
  };

  const handleToggleMicrophone = useCallback(() => {
    const stream = streamRef.current;

    if (!stream) {
      return;
    }

    const audioTracks = stream.getAudioTracks();

    if (audioTracks.length === 0) {
      return;
    }

    const currentlyEnabled = audioTracks.some((track) => track.enabled);

    const nextEnabled = !currentlyEnabled;

    audioTracks.forEach((track) => {
      track.enabled = nextEnabled;
    });

    setMicEnabled(nextEnabled);

    console.log(nextEnabled ? "🎤 Microphone enabled" : "🔇 Microphone muted");
  }, []);

  const handleToggleCamera = useCallback(() => {
    const stream = streamRef.current;

    if (!stream) {
      return;
    }

    const videoTracks = stream.getVideoTracks();

    if (videoTracks.length === 0) {
      return;
    }

    const currentlyEnabled = videoTracks.some((track) => track.enabled);

    const nextEnabled = !currentlyEnabled;

    videoTracks.forEach((track) => {
      track.enabled = nextEnabled;
    });

    setCameraEnabled(nextEnabled);

    console.log(nextEnabled ? "📹 Camera enabled" : "🚫 Camera disabled");
  }, []);

  const handleFlipCamera = useCallback(async () => {
    if (!streamRef.current || switchingCamera) {
      return;
    }

    setSwitchingCamera(true);

    const nextFacingMode = facingMode === "user" ? "environment" : "user";

    try {
      const newCameraStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: {
            ideal: nextFacingMode,
          },

          width: {
            ideal: 720,
          },

          height: {
            ideal: 1280,
          },

          aspectRatio: {
            ideal: 9 / 16,
          },
        },

        audio: false,
      });

      const newVideoTrack = newCameraStream.getVideoTracks()[0];

      if (!newVideoTrack) {
        throw new Error("Unable to open the other camera.");
      }

      /*
       * Respect current camera
       * ON/OFF status
       */
      newVideoTrack.enabled = cameraEnabled;

      /*
       * Replace video track
       * for every connected viewer
       */
      const replacements = [];

      viewerPeersRef.current.forEach((peer) => {
        const videoSender = peer
          .getSenders()
          .find((sender) => sender.track?.kind === "video");

        if (videoSender) {
          replacements.push(videoSender.replaceTrack(newVideoTrack));
        }
      });

      await Promise.all(replacements);

      /*
       * Replace local camera track
       */
      const currentStream = streamRef.current;

      const oldVideoTrack = currentStream.getVideoTracks()[0];

      if (oldVideoTrack) {
        currentStream.removeTrack(oldVideoTrack);

        oldVideoTrack.stop();
      }

      currentStream.addTrack(newVideoTrack);

      /*
       * Update host preview
       */
      if (videoRef.current) {
        videoRef.current.srcObject = currentStream;

        try {
          await videoRef.current.play();
        } catch (playError) {
          console.warn("Unable to resume camera preview:", playError);
        }
      }

      setFacingMode(nextFacingMode);

      console.log("🔄 Camera switched:", nextFacingMode);
    } catch (error) {
      console.error("Unable to switch camera:", error);

      setCameraError(error?.message || "Unable to switch camera.");
    } finally {
      setSwitchingCamera(false);
    }
  }, [facingMode, cameraEnabled, switchingCamera]);

  // ==========================
  // END LIVE
  // ==========================

  const handleEndLive = async () => {
    if (endingLive) return;

    if (!authorizationHeader || !activeLive?.id) {
      return;
    }

    setApiError("");
    setEndingLive(true);

    try {
      // --------------------------
      // Update backend first
      // --------------------------

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/live/${activeLive.id}/end`,

        {
          method: "PATCH",

          headers: {
            Authorization: authorizationHeader,
          },
        },
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.message || `Unable to end live: ${response.status}`,
        );
      }

      // --------------------------
      // Tell all viewers
      // --------------------------

      if (liveStompClientRef.current?.connected) {
        liveStompClientRef.current.publish({
          destination: `/app/live/${activeLive.id}`,

          body: JSON.stringify({
            liveId: activeLive.id,

            fromUserId: currentUser?.id,

            type: "END",
          }),
        });

        console.log("📤 END sent to viewers");
      }

      // --------------------------
      // Close WebRTC
      // --------------------------

      closeAllViewerPeers();

      liveViewersRef.current.clear();

      setViewerCount(0);

      setLiveComments([]);
      setLiveGifts([]);
      setLiveReactions([]);

      // --------------------------
      // Update UI
      // --------------------------

      setActiveLive(data);

      setShowEndConfirm(false);

      setShowViewers(false);

      acceptedGuestRef.current = null;

      setActiveGuest(null);

      setGuestConnected(false);

      if (guestRemoteVideoRef.current) {
        guestRemoteVideoRef.current.srcObject = null;
      }

      setLiveViewerUsers([]);

      setLiveReactions([]);
      setLiveComments([]);
      setLiveGifts([]);

      stopCamera();

      window.dispatchEvent(new CustomEvent("refresh-live-sessions"));

      console.log("⚫ Live session ended:", data);
    } catch (error) {
      console.error("Unable to end live:", error);

      setApiError(
        error?.message || t("live.endError", "Unable to end the live session."),
      );
    } finally {
      setEndingLive(false);
    }
  };

  // ==========================
  // BACK
  // ==========================

  const handleBack = () => {
    if (String(activeLive?.status).toUpperCase() === "LIVE") {
      setApiError(
        t(
          "live.endBeforeLeaving",
          "Please end your live before leaving this page.",
        ),
      );

      return;
    }

    closeAllViewerPeers();

    stopCamera();

    navigate(-1);
  };

  // ==========================
  // LIVE STATUS
  // ==========================

  const isLive = String(activeLive?.status).toUpperCase() === "LIVE";

  // ==========================
  // JSX
  // ==========================

  const { seconds: liveSecondsRemaining, continueLive } = useLiveCountdown(
    isLive ? activeLive.id : null,
    () => {
      // Stop broadcasting immediately, even if the end request fails.
      closeAllViewerPeers();
      stopCamera();
      handleEndLive();
    },
  );

  return (
    <main className={`go-live-page ${isLive ? "is-live-mode" : ""}`}>
      {isLive && liveSecondsRemaining > 0 && liveSecondsRemaining <= 5 * 60 && (
        <div
          onClick={continueLive}
          role="button"
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 999999,
            width: "220px",
            height: "220px",
            borderRadius: "50%",
            background: "black",
            border: "6px solid #00eaff",
            color: "#00eaff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          {/* COUNT */}
          <div
            style={{
              fontSize: "70px",
              fontWeight: "bold",
              lineHeight: "1",
            }}
          >
            {String(Math.floor(liveSecondsRemaining / 60)).padStart(2, "0")}:{String(liveSecondsRemaining % 60).padStart(2, "0")}
          </div>

          {/* SECONDS */}

          {/* CLICK MESSAGE */}
          {/* CLICK MESSAGE */}
          <div
            style={{
              fontSize: "14px",
              color: "white",
            }}
          >
            {t("liveCountdown.clickToStayLive", "Click here to stay live")}
          </div>
        </div>
      )}

      {/* LIVE SESSION COUNTER */}
      {isLive && (
        <div
          className={`go-live-countdown ${
            liveSecondsRemaining <= 10 ? "is-urgent" : ""
          }`}
        >
          <div className="go-live-countdown-heading">
            <FaBroadcastTower />
            <span>{t("liveCountdown.session", "LIVE SESSION")}</span>
          </div>

          <strong className="go-live-countdown-time">
            {String(Math.floor(liveSecondsRemaining / 60)).padStart(2, "0")}:
            {String(liveSecondsRemaining % 60).padStart(2, "0")}
          </strong>

        </div>
      )}

      {/* ======================
                HEADER
            ====================== */}

      <header className="go-live-header">
        <button
          type="button"
          className="go-live-back"
          onClick={handleBack}
          aria-label={t("common.back", "Back")}
        >
          <FaArrowLeft />
        </button>

        <div>
          <h1>{t("live.goLive", "Go Live")}</h1>

          <p>
            {t("live.goLiveSubtitle", "Share a live video with other users.")}
          </p>
        </div>
      </header>

      {/* ======================
                VIEWER JOIN MESSAGE
            ====================== */}
      {guestRequest && (
        <div className="guest-request-overlay">
          <div className="guest-request-card">
            <div className="guest-request-pulse">
              {guestRequest.viewerImage ? (
                <img
                  src={guestRequest.viewerImage}
                  alt={guestRequest.viewerName || "Guest"}
                  className="guest-request-avatar"
                />
              ) : (
                <div className="guest-request-avatar-fallback">
                  {guestRequest.viewerName?.charAt(0)?.toUpperCase() || "?"}
                </div>
              )}
            </div>

            <div className="guest-request-content">
              <span className="guest-request-badge">
                🎥 {t("live.guestRequestBadge", "Join request")}
              </span>

              <h3>{guestRequest.viewerName}</h3>

              <p>
                {t(
                  "live.guestWantsToJoin",
                  "wants to join your live with camera and microphone.",
                )}
              </p>
            </div>

            <div className="guest-request-actions">
              <button
                type="button"
                className="guest-request-decline"
                onClick={handleDeclineGuest}
                disabled={guestRequestProcessing}
              >
                {t("live.declineGuest", "Decline")}
              </button>

              <button
                type="button"
                className="guest-request-accept"
                onClick={handleAcceptGuest}
                disabled={guestRequestProcessing}
              >
                <FaVideo />

                {t("live.acceptGuest", "Accept")}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ======================
                MAIN LAYOUT
            ====================== */}

      <div className="go-live-layout">
        {/* ==================
                    CAMERA PREVIEW
                ================== */}

        <section className="go-live-preview-card">
          <div className="go-live-video-wrapper">
            <video
              ref={videoRef}
              className={`go-live-video ${
                facingMode === "user" ? "is-front-camera" : "is-back-camera"
              }`}
              autoPlay
              muted
              playsInline
            />

            {activeGuest && (
              <div
                className={`host-guest-window ${
                  guestConnected ? "is-connected" : "is-connecting"
                }`}
              >
                <button
                  type="button"
                  className="host-remove-guest"
                  onClick={handleRemoveGuest}
                  aria-label={t("live.removeGuest", "Remove guest")}
                >
                  ×
                </button>

                <video
                  ref={guestRemoteVideoRef}
                  className="host-guest-video"
                  autoPlay
                  playsInline
                />

                {!guestConnected && (
                  <div className="guest-connecting-overlay">
                    {activeGuest.viewerImage ? (
                      <img
                        src={activeGuest.viewerImage}
                        alt={activeGuest.viewerName}
                      />
                    ) : (
                      <div className="guest-connecting-fallback">
                        {activeGuest.viewerName?.charAt(0)?.toUpperCase() ||
                          "?"}
                      </div>
                    )}

                    <div className="guest-loading-dots">
                      <span />
                      <span />
                      <span />
                    </div>

                    <small>{t("live.guestConnecting", "Connecting...")}</small>
                  </div>
                )}

                <div className="host-guest-name">
                  <span className="guest-live-dot" />

                  {activeGuest.viewerName}
                </div>
              </div>
            )}

            {isLive && (
              <div className="live-reactions-layer" aria-hidden="true">
                {liveReactions.map((reaction) => (
                  <div
                    key={reaction.id}
                    className="live-floating-reaction"
                    style={{
                      right: `${reaction.right}px`,

                      left: "auto",

                      fontSize: `${reaction.size}px`,

                      animationDuration: `${reaction.duration}s`,
                    }}
                  >
                    <strong>{reaction.name}</strong>

                    <FaHeart />
                  </div>
                ))}
              </div>
            )}

            {/* ==========================
    LIVE COMMENTS
========================== */}

            {isLive && (
              <div className="go-live-comments-layer">
                {liveComments.map((comment) => (
                  <div key={comment.id} className="go-live-comment">
                    <div className="go-live-comment-avatar">
                      {comment.userName?.charAt(0)?.toUpperCase() || "U"}
                    </div>

                    <div className="go-live-comment-body">
                      <strong>{comment.userName}</strong>

                      <span>{comment.message}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ==========================
    LIVE GIFTS
========================== */}

            {isLive && (
              <div className="go-live-gifts-layer" aria-hidden="true">
                {liveGifts.map((gift) => (
                  <div
                    key={gift.id}
                    className="go-live-gift-animation"
                    style={{
                      left: `${gift.left}%`,
                    }}
                  >
                    <div className="go-live-gift-emoji">{gift.emoji}</div>

                    <div className="go-live-gift-info">
                      <strong>{gift.userName}</strong>

                      <span>sent {gift.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {isLive && (
              <div className="go-live-live-badge">
                <span />
                LIVE
              </div>
            )}

            {isLive && (
              <button
                type="button"
                className="go-live-viewer-count"
                onClick={() => setShowViewers((previous) => !previous)}
                aria-label="Show viewers"
              >
                <FaEye />

                <span>{viewerCount}</span>
              </button>
            )}

            {isLive && (
              <div className="go-live-earnings-badge">
                <FaWallet />

                <span>{creatorEarnings.toFixed(2)}</span>
              </div>
            )}

            {isLive && showViewers && (
              <div className="go-live-viewers-popup">
                <div className="go-live-viewers-popup-header">
                  <strong>Viewers</strong>

                  <span>{viewerCount}</span>
                </div>

                {liveViewerUsers.length === 0 ? (
                  <div className="go-live-viewers-popup-empty">
                    No viewers yet
                  </div>
                ) : (
                  <div className="go-live-viewers-popup-list">
                    {liveViewerUsers.map((viewer) => (
                      <div key={viewer.id} className="go-live-popup-viewer">
                        {viewer.image ? (
                          <img src={viewer.image} alt={viewer.name} />
                        ) : (
                          <div className="go-live-popup-avatar">
                            {viewer.name?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                        )}

                        <strong>{viewer.name}</strong>

                        <button
                          type="button"
                          onClick={() => handleKickViewer(viewer.id)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {isLive && (
              <div className="go-live-host-controls">
                {/* MICROPHONE */}

                <button
                  type="button"
                  className={!micEnabled ? "is-disabled" : ""}
                  onClick={handleToggleMicrophone}
                  aria-label={
                    micEnabled ? "Mute microphone" : "Unmute microphone"
                  }
                  title={micEnabled ? "Mute microphone" : "Unmute microphone"}
                >
                  {micEnabled ? <FaMicrophone /> : <FaMicrophoneSlash />}
                </button>

                {/* CAMERA */}

                <button
                  type="button"
                  className={!cameraEnabled ? "is-disabled" : ""}
                  onClick={handleToggleCamera}
                  aria-label={
                    cameraEnabled ? "Turn camera off" : "Turn camera on"
                  }
                  title={cameraEnabled ? "Turn camera off" : "Turn camera on"}
                >
                  {cameraEnabled ? <FaVideo /> : <FaVideoSlash />}
                </button>

                {/* FLIP CAMERA */}

                <button
                  type="button"
                  className={switchingCamera ? "is-switching" : ""}
                  disabled={switchingCamera}
                  onClick={handleFlipCamera}
                  aria-label="Switch camera"
                  title="Switch camera"
                >
                  <FaSyncAlt />
                </button>
              </div>
            )}

            {!cameraReady && (
              <div className="go-live-camera-placeholder">
                {cameraLoading ? (
                  <>
                    <div className="go-live-spinner" />

                    <p>{t("live.openingCamera", "Opening camera...")}</p>
                  </>
                ) : (
                  <>
                    <FaVideoSlash />

                    <p>
                      {t("live.cameraUnavailable", "Camera is not available.")}
                    </p>

                    <button type="button" onClick={startCamera}>
                      <FaVideo />

                      {t("live.enableCamera", "Enable camera")}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {cameraError && <div className="go-live-error">{cameraError}</div>}

          {isLive && (
            <div className="go-live-active-information">
              <div>
                <span>{t("live.currentlyLive", "You are live now")}</span>

                <strong>{activeLive.title}</strong>
              </div>

              <span className="go-live-session-id">ID: {activeLive.id}</span>
            </div>
          )}
        </section>

        {/* ==================
                    LIVE SETTINGS
                ================== */}

        <section className="go-live-settings-card">
          <div className="go-live-settings-heading-row">
            {!isLive && activeLive?.status !== "ENDED" && (
              <div className="go-live-start-row">
                <label
                  className="go-live-field go-live-title-field"
                  htmlFor="live-title"
                >
                  <span>{t("live.title", "Title")}</span>

                  <input
                    id="live-title"
                    type="text"
                    value={title}
                    maxLength={150}
                    placeholder={t(
                      "live.titlePlaceholder",
                      "What is your live about?",
                    )}
                    onChange={(event) => setTitle(event.target.value)}
                  />

                  <small>{title.length}/150</small>
                </label>

                <button
                  type="button"
                  className="go-live-start-button go-live-start-button-inline"
                  disabled={startingLive || !cameraReady}
                  onClick={handleStartLive}
                >
                  <FaBroadcastTower />

                  {startingLive
                    ? t("live.starting", "Starting...")
                    : t("live.startLive", "Start Live")}
                </button>
              </div>
            )}
          </div>

          {isLive && (
            <div className="go-live-viewers-panel">
              <div className="go-live-viewers-title">
                <div>
                  <FaEye />

                  <strong>{t("live.viewers", "Viewers")}</strong>
                </div>

                <span>{viewerCount}</span>
              </div>

              {liveViewerUsers.length === 0 ? (
                <p className="go-live-no-viewers">
                  {t("live.noViewers", "No viewers yet")}
                </p>
              ) : (
                <div className="go-live-viewers-list">
                  {liveViewerUsers.map((viewer) => (
                    <div className="go-live-viewer-row" key={viewer.id}>
                      {viewer.image ? (
                        <img src={viewer.image} alt={viewer.name} />
                      ) : (
                        <div className="go-live-viewer-avatar">
                          {viewer.name?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                      )}

                      <strong>{viewer.name}</strong>

                      <button
                        type="button"
                        onClick={() => handleKickViewer(viewer.id)}
                      >
                        <FaUserTimes />
                        {t("live.remove", "Remove")}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ==================
                        ENDED
                    ================== */}

          {activeLive?.status === "ENDED" && (
            <div className="go-live-ended">
              <FaStopCircle />

              <h3>{t("live.liveEnded", "Live ended")}</h3>

              <p>
                {t(
                  "live.liveEndedDescription",
                  "Your live session has ended successfully.",
                )}
              </p>
            </div>
          )}

          {/* ==================
                        ERROR
                    ================== */}

          {apiError && (
            <div className="go-live-error" role="alert">
              {apiError}
            </div>
          )}

          {/* ==================
    END / RETURN
================== */}

          {isLive ? (
            <button
              type="button"
              className="go-live-end-button"
              disabled={endingLive}
              onClick={() => setShowEndConfirm(true)}
            >
              <FaStopCircle />

              {endingLive
                ? t("live.ending", "Ending...")
                : t("live.endLive", "End Live")}
            </button>
          ) : activeLive?.status === "ENDED" ? (
            <button
              type="button"
              className="go-live-back-home-button"
              onClick={() => navigate("/suggestions")}
            >
              {t("live.returnToApp", "Return to app")}
            </button>
          ) : null}
        </section>
      </div>

      {showEndConfirm && isLive && (
        <div
          className="go-live-end-confirm-backdrop"
          onClick={() => !endingLive && setShowEndConfirm(false)}
        >
          <div
            className="go-live-end-confirm"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="go-live-end-confirm-icon">
              <FaStopCircle />
            </div>

            <h3>End this live?</h3>

            <p>Your live stream will end for everyone currently watching.</p>

            <div className="go-live-end-confirm-actions">
              <button
                type="button"
                className="go-live-end-cancel"
                disabled={endingLive}
                onClick={() => setShowEndConfirm(false)}
              >
                Cancel
              </button>

              <button
                type="button"
                className="go-live-end-confirm-button"
                disabled={endingLive}
                onClick={handleEndLive}
              >
                <FaStopCircle />

                {endingLive ? "Ending..." : "End Live"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default GoLivePage;
